import { useMemo } from 'react';

import type { LEProductAccessGroupParams } from './components/InviteTray/LEProductAccessGroup';
import type { ProductDefinition } from './components/InviteTray/productCatalog';
import type { MemberRole, MemberRoleMap } from './utils/constants';
import type { UseMultiLEProductSelectionReturn } from './useMultiLEProductSelection';

const LE_ICON_BACKGROUND = 'rgb(var(--gray5))';

type LegalEntity = {
  legalEntityId?: string;
  legalEntityName?: string;
};

type LocalizedProduct = Omit<ProductDefinition, 'titleMessage' | 'descriptionMessage'> & {
  title: string;
  description: string;
};

type UseMultiLEProductAccessEntitiesParams = {
  legalEntities: readonly LegalEntity[];
  localizedProducts: readonly LocalizedProduct[];
  rolesByProduct: Record<string, MemberRoleMap[]>;
  selection: Pick<
    UseMultiLEProductSelectionReturn,
    | 'getProductSelectionForEntity'
    | 'getRolesForEntity'
    | 'handleEntitySelectionToggle'
    | 'handleProductCheckedChange'
    | 'handleProductRoleToggle'
  >;
};

export function useMultiLEProductAccessEntities({
  legalEntities,
  localizedProducts,
  rolesByProduct,
  selection,
}: UseMultiLEProductAccessEntitiesParams): LEProductAccessGroupParams[] {
  const {
    getProductSelectionForEntity,
    getRolesForEntity,
    handleEntitySelectionToggle,
    handleProductCheckedChange,
    handleProductRoleToggle,
  } = selection;

  return useMemo(
    () =>
      legalEntities
        .filter((le): le is LegalEntity & { legalEntityId: string } => Boolean(le.legalEntityId))
        .map((le) => {
          const entityId = le.legalEntityId;
          const selectedRolesByProduct = getRolesForEntity(entityId);

          return {
            entityRow: {
              entityId,
              title: le.legalEntityName || entityId,
              iconName: 'corporation' as const,
              iconBackground: LE_ICON_BACKGROUND,
              selectionState: getProductSelectionForEntity(entityId),
              onSelectionToggle: (isSelected: boolean) =>
                handleEntitySelectionToggle(entityId, isSelected),
            },
            productRows: localizedProducts.map(
              ({ productKey, iconName, iconBackground, title: productTitle, description }) => ({
                productKey,
                iconName,
                iconBackground,
                title: productTitle,
                description,
                roleOptions: rolesByProduct[productKey] ?? [],
                selectedRoleValues: selectedRolesByProduct[productKey] ?? [],
                onCheckedChange: (isChecked: boolean) =>
                  handleProductCheckedChange(entityId, productKey, isChecked),
                onRoleToggle: (role: MemberRole, isSelected: boolean) =>
                  handleProductRoleToggle(entityId, productKey, role, isSelected),
              }),
            ),
          };
        }),
    [
      getProductSelectionForEntity,
      getRolesForEntity,
      handleEntitySelectionToggle,
      handleProductCheckedChange,
      handleProductRoleToggle,
      legalEntities,
      localizedProducts,
      rolesByProduct,
    ],
  );
}
